import type { FC, ChangeEvent } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CommonTextField from '@/shared/components/CommonTextField';
import { useFormikContext } from 'formik';
import type { IBasicCreativeFormValues } from '@/editor/shared/components/forms/creatives/models/basic-creative-form-values.model';
import debounce from 'lodash/debounce';
import type { PlatformType } from '@/editor/shared/constants/available-platforms';
import { AvailablePlatformsEnum } from '@/editor/shared/constants/available-platforms';
import type { ICreativeFormsStatePerPlatform } from '@/editor/store/reducers/creative-step.slice';
import { setFieldOfCreativeFormForPlatform } from '@/editor/store/reducers/creative-step.slice';
import CreativeTypesEnum from '@/editor/shared/constants/creatives-types.enum';
import { useAppDispatch, useAppSelector } from '@/store/hooks/redux';

interface PropertyDetailsFormProps {
  platform: PlatformType;
}

const PropertyDetailsForm: FC<PropertyDetailsFormProps> = ({ platform }) => {
  const { t } = useTranslation();
  const { getFieldMeta, handleChange } = useFormikContext<IBasicCreativeFormValues>();
  const dispatch = useAppDispatch();
  const selectedCreativeType = useAppSelector((state) => state.creativeStepSliceReducer.selectedCreativeType);

  const handleFieldChange = (value: string | number | boolean, field: any) => {
    debounce((platform: PlatformType, field: keyof IBasicCreativeFormValues, value: string | number | boolean) => {
      const creativeType =
        platform === AvailablePlatformsEnum.DELTA ||
        (platform === AvailablePlatformsEnum.BIDTHEATRE && selectedCreativeType === CreativeTypesEnum.CAROUSEL) ||
        platform === AvailablePlatformsEnum.BOARDS
          ? CreativeTypesEnum.SINGLE
          : selectedCreativeType;

      return dispatch(
        setFieldOfCreativeFormForPlatform({
          platform,
          creativeType: creativeType as keyof ICreativeFormsStatePerPlatform,
          field,
          value,
        })
      );
    }, 500)(platform, field, value);
  };

  const handleFieldChangeWrapper = (e: ChangeEvent<HTMLInputElement>, field: string) => {
    handleChange(e);
    handleFieldChange(e.target.value, field);
  };

  return (
    <Box sx={{ mb: 2, p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CommonTextField
            size="small"
            type="text"
            name="areaInUsage"
            label={t('areaInUsage')}
            value={getFieldMeta('areaInUsage').value as string}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChangeWrapper(e, 'areaInUsage')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CommonTextField
            size="small"
            type="text"
            name="propertyDescription2"
            label={t('viewDate')}
            value={getFieldMeta('propertyDescription2').value as string}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChangeWrapper(e, 'propertyDescription2')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CommonTextField
            size="small"
            type="text"
            name="propertyStandard"
            label={t('propertyStandard')}
            value={getFieldMeta('propertyStandard').value as string}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChangeWrapper(e, 'propertyStandard')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CommonTextField
            size="small"
            type="text"
            name="bedroom"
            label={t('noOfBedroom')}
            value={getFieldMeta('bedroom').value as string}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChangeWrapper(e, 'bedroom')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CommonTextField
            size="small"
            type="text"
            name="officeName"
            label={t('officeName')}
            value={getFieldMeta('officeName').value as string}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChangeWrapper(e, 'officeName')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CommonTextField
            size="small"
            type="text"
            name="employeeName"
            label={t('brokerName')}
            value={getFieldMeta('employeeName').value as string}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChangeWrapper(e, 'employeeName')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CommonTextField
            size="small"
            type="text"
            name="employeePhone"
            label={t('brokerNumber')}
            value={getFieldMeta('employeePhone').value as string}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChangeWrapper(e, 'employeePhone')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CommonTextField
            size="small"
            type="email"
            name="employeeEmail"
            label={t('brokerEmail')}
            value={getFieldMeta('employeeEmail').value as string}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleFieldChangeWrapper(e, 'employeeEmail')}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PropertyDetailsForm;
